import { Draggable } from '@hello-pangea/dnd';
import classNames from 'classnames';
import { useEffect } from 'react';
import { Popover } from '@meitu/saas-ui';
import IconFont from '@meitu/xdesign-icon-font';
import { LayerView, useFramework } from '@/framework';
import { LayerType, type ILayerProps } from '@/version-types';
import { StageToolbar } from '../../../stage-toolbar';
import { HIERARCHY_EVENTS, THUMBNAIL_SIZE } from '../../constants';
import { type HierarchyMenuItem } from '../../types';
import { getLayerTitle } from '../../utils';
import { Menu } from '../menu';
import './style.less';

type Props = React.ComponentProps<'div'> & {
    active: boolean;
    layer: ILayerProps;
    index: number;
    ancestorLayerLocked?: boolean;
    menuOpen?: boolean;
    onClick: () => void;
    onOpenMenu?: () => void;
    onUpdate: () => void;
    onCloseMenu?: () => void;
    onHover?: (flag: boolean) => void;
};

export const Item: React.FC<Props> = ({
    className,
    style,
    active,
    layer,
    index,
    ancestorLayerLocked = false,
    menuOpen = false,
    onClick,
    onOpenMenu,
    onUpdate,
    onCloseMenu,
    onHover,
}) => {
    const framework = useFramework();
    const pluginInstance = framework.getInstance(StageToolbar);
    const { menuItems: menus, preventHiddenTypes } = pluginInstance.getHierarchyConfig();
    const availableMenus = menus.filter(({ predicate }) => (predicate ? predicate(layer) : true));

    const {
        width: layerWidth,
        height: layerHeight,
        left: layerLeft,
        top: layerTop,
        layerType,
        children,
        lock,
        hidden,
    } = layer;
    const isGroup = layerType === LayerType.GROUP_LAYER;
    const isBG = layerType === LayerType.BACKGROUND_LAYER;
    const isMain = layerType === LayerType.MAIN_LAYER;
    const isStatic = isBG || isMain;
    const isDragDisabled = isStatic || ancestorLayerLocked || lock;
    const childrenLen = isGroup ? children.length : 0;

    const title = getLayerTitle(layer);

    const layerVIewExtraProps = {
        identity: framework.identity,
        isNavigator: true,
        isThumbnail: true,
        ignoreHidden: true,
    };

    const scale = Math.min(THUMBNAIL_SIZE / layerWidth, THUMBNAIL_SIZE / layerHeight); // 图层需要在 thumbnail 中完整显示的缩放比例
    const stageStyle: React.CSSProperties = (() => {
        const deltaX = (THUMBNAIL_SIZE * (1 / scale) - layerWidth) / 2;
        const deltaY = (THUMBNAIL_SIZE * (1 / scale) - layerHeight) / 2;

        const translateX = -layerLeft + deltaX;
        const translateY = -layerTop + deltaY;

        return {
            transform: `scale(${scale}) translate(${translateX}px, ${translateY}px)`,
        };
    })();

    const handleClick = () => {
        if (active && !isGroup) return;
        onClick();
    };

    const handleHover = (flag: boolean) => {
        onHover?.(flag);
    };

    const handleOpenMenu = (event: React.MouseEvent) => {
        event.stopPropagation();
        if (onOpenMenu) onOpenMenu();
    };

    const handleUnlock = (event: React.MouseEvent) => {
        event.stopPropagation();
        framework.events.emit(HIERARCHY_EVENTS.LAYER_OPERATION, layer, 'unlock');
    };

    const handleCloseMenu = () => {
        if (onCloseMenu) onCloseMenu();
    };

    const handleOperate = (item: HierarchyMenuItem) => {
        framework.events.emit(HIERARCHY_EVENTS.LAYER_OPERATION, layer, item.operation);
    };

    useEffect(() => {
        const listener = framework.events.onRerenderLayer(layer.id, () => {
            onUpdate();
        });

        return () => {
            framework.events.off(listener);
        };
    }, []);

    const renderActions = () => {
        if (isStatic || ancestorLayerLocked) return null;

        if (lock) {
            return (
                <div className="hierarchy-item__actions">
                    <span className="hierarchy-item__lock" onClick={handleUnlock}>
                        <IconFont name="LockBold" fontSize={12} />
                    </span>
                </div>
            );
        }

        if (availableMenus.length === 0) return null;

        return (
            <div className="hierarchy-item__actions">
                <Popover
                    trigger="click"
                    open={menuOpen}
                    showArrow={false}
                    placement="bottom"
                    overlayClassName="hierarchy-item__menu"
                    content={<Menu menus={menus} onOperate={handleOperate} onClickAway={handleCloseMenu} />}
                >
                    <IconFont name="EllipsisBold" fontSize={12} onClick={handleOpenMenu} />
                </Popover>
            </div>
        );
    };

    const handleHidden = (hidden: boolean, event: React.MouseEvent) => {
        event.stopPropagation();
        framework.events.emit(HIERARCHY_EVENTS.LAYER_OPERATION, layer, hidden ? 'hide' : 'show');
    };

    const renderItem = (extraProps: Record<string, unknown> = {}, extraStyles: React.CSSProperties = {}) => {
        const { isDragging, ...restDOMProps } = extraProps;

        return (
            <div
                {...restDOMProps}
                className={classNames(
                    'hierarchy-item',
                    {
                        'hierarchy-item--grouped': isGroup,
                        'hierarchy-item--active': active,
                        'hierarchy-item--locked': lock,
                        'hierarchy-item--static': isStatic,
                        'hierarchy-item--dragging': isDragging,
                        'hierarchy-item--hidden': hidden,
                    },
                    className,
                )}
                style={{
                    ...extraStyles,
                    ...style,
                }}
                onClick={handleClick}
                onMouseEnter={() => handleHover(true)}
                onMouseLeave={() => handleHover(false)}
            >
                <div className="hierarchy-item__content">
                    <div className="hierarchy-item__info">
                        {/* 拖拽图标 */}
                        {!isStatic ? (
                            <div className="hierarchy-item__drag">
                                {!lock && !ancestorLayerLocked ? <IconFont name="Drag" fontSize={12} /> : null}
                            </div>
                        ) : null}

                        {/* 图层缩略图 */}
                        <div className="hierarchy-item__thumbnail">
                            {/* stage 容器用于模拟舞台，通过 transform 来实现缩放和平移 */}
                            <div className="hierarchy-item__stage" style={stageStyle}>
                                <LayerView layer={layer} extraProps={layerVIewExtraProps} />
                            </div>
                        </div>

                        {/* 图层名称 */}
                        <div className="hierarchy-item__name">{title}</div>

                        {/* 隐藏 */}
                        {!preventHiddenTypes.includes(layer.layerType) && (
                            <div className="hierarchy-item__hidden" onClick={(e) => handleHidden(!layer.hidden, e)}>
                                {layer.hidden ? (
                                    <IconFont name="InvisibleBold" fontSize={12} />
                                ) : (
                                    <IconFont name="VisibleBold" fontSize={12} />
                                )}
                            </div>
                        )}
                    </div>

                    {/* 菜单 */}
                    {renderActions()}
                </div>

                {/* 组图层角标：显示子图层数量 */}
                {childrenLen > 0 ? (
                    <div className="hierarchy-item__num">
                        <span>{childrenLen}</span>
                    </div>
                ) : null}

                {/* 组图层底部装饰 */}
                {isGroup ? (
                    <div className="hierarchy-item__bottom">
                        <svg xmlns="http://www.w3.org/2000/svg" width="184" height="4" viewBox="0 0 184 4">
                            <path d="M184.002 0C183.178 2.33011 180.957 4 178.345 4H5.65723C3.04498 4 0.823821 2.33011 0 0H184.002Z" />
                        </svg>
                    </div>
                ) : null}
            </div>
        );
    };

    if (isBG || isMain) return renderItem();

    return (
        <Draggable draggableId={layer.id} index={index} isDragDisabled={isDragDisabled}>
            {(provided, snapshot) =>
                renderItem(
                    {
                        ref: provided.innerRef,
                        ...provided.draggableProps,
                        ...provided.dragHandleProps,
                        isDragging: snapshot.isDragging,
                    },
                    provided.draggableProps.style,
                )
            }
        </Draggable>
    );
};
